import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ExploreAllProjects.css";
import { FiArrowUpRight, FiSliders } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { projectsData } from "../../../../data/projectsData";
import { useTranslation } from "react-i18next";
import { getProjects } from "../../../../api/projects";
import { resolveMediaUrl } from "../../../../utils/media";

const ITEMS_PER_PAGE = 3;

const ExploreAllProjects = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedSector, setSelectedSector] = useState("All");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [apiProjects, setApiProjects] = useState([]);
  const sectorSelectRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const loadProjects = async () => {
      try {
        const data = await getProjects({ language: String(i18n.language || "en").toLowerCase() });
        if (isMounted) {
          setApiProjects(Array.isArray(data) ? data : []);
        }
      } catch (_error) {
        if (isMounted) {
          setApiProjects([]);
        }
      }
    };
    loadProjects();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const mappedProjects = useMemo(() => {
    if (!Array.isArray(apiProjects) || !apiProjects.length) return projectsData;
    const mapped = apiProjects
      .map((project) => {
        const routeId = String(project?.projectKey || project?.id || "");
        if (!routeId) return null;
        const gallery = Array.isArray(project?.images) ? project.images : [];
        const firstGallery = gallery[0]?.imageUrl || "";
        const imageUrl = resolveMediaUrl(project?.coverImageUrl) || resolveMediaUrl(firstGallery) || "";
        return {
          id: routeId,
          title: project?.title || "Project",
          description: project?.description || "",
          highlight: project?.highlight || "",
          location: project?.location || "",
          sector: project?.sector || "Sector name",
          image: imageUrl || projectsData[0]?.image,
          featured: Boolean(project?.featured),
        };
      })
      .filter(Boolean);
    return mapped.length ? mapped : projectsData;
  }, [apiProjects]);

  const sectors = useMemo(
    () => ["All", ...new Set(mappedProjects.map((project) => project.sector))],
    [mappedProjects]
  );

  const filteredProjects = useMemo(() => {
    if (selectedSector === "All") return mappedProjects;
    return mappedProjects.filter((project) => project.sector === selectedSector);
  }, [selectedSector, mappedProjects]);

  const visibleProjects = filteredProjects.slice(0, visibleCount);
  const canLoadMore = visibleCount < filteredProjects.length;

  const handleSectorChange = (e) => {
    setSelectedSector(e.target.value);
    setVisibleCount(ITEMS_PER_PAGE);
    e.target.blur();
  };

  const handleLoadMore = () => {
    if (isLoadingMore || !canLoadMore) return;
    setIsLoadingMore(true);
    window.setTimeout(() => {
      setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
      setIsLoadingMore(false);
    }, 900);
  };

  const openSectorDropdown = () => {
    const selectEl = sectorSelectRef.current;
    if (!selectEl) return;
    selectEl.focus();
    if (typeof selectEl.showPicker === "function") {
      selectEl.showPicker();
    }
  };

  return (
    <section className="exploreAllProjects" aria-label="Explore all projects">
      <div className="exploreAllProjects-top">
        <h2 className="exploreAllProjects-title">Explore all projects</h2>

        <div
          className="exploreAllProjects-filterWrap"
          onClick={(e) => {
            if (e.target.closest("select")) return;
            openSectorDropdown();
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openSectorDropdown();
            }
          }}
          aria-label="Open sector filter"
        >
          <FiSliders className="exploreAllProjects-filterIcon" aria-hidden />
          <label htmlFor="explore-projects-sector">Sectors:</label>
          <select
            id="explore-projects-sector"
            ref={sectorSelectRef}
            value={selectedSector}
            onChange={handleSectorChange}
            aria-label="Filter projects by sector"
            style={{ width: `${Math.max(selectedSector.length + 0.5, 3)}ch` }}
          >
            {sectors.map((sector) => (
              <option value={sector} key={sector}>
                {sector}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="exploreAllProjects-list">
        {visibleProjects.map((project) => (
          <article
            className={`exploreAllProjects-item ${
              project.featured ? "is-featured" : ""
            }`}
            key={project.id}
            onClick={() => navigate(`/allprojects/${project.id}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/allprojects/${project.id}`);
              }
            }}
          >
            <div className="exploreAllProjects-thumbWrap">
              <img
                src={project.image}
                alt={project.title}
                className="exploreAllProjects-thumb"
              />
            </div>

            <div className="exploreAllProjects-content">
              <h3>{project.title}</h3>
              <p className="exploreAllProjects-desc">{project.description}</p>
              <p className="exploreAllProjects-locationTitle">Location</p>
              <p className="exploreAllProjects-location">{project.location}</p>
            </div>

            <Link
              to={`/allprojects/${project.id}`}
              className="exploreAllProjects-openBtn"
              aria-label={`Open ${project.title}`}
              onClick={(e) => e.stopPropagation()}
            >
              <FiArrowUpRight />
            </Link>
          </article>
        ))}
      </div>

      {(canLoadMore || isLoadingMore) && (
        <div className="exploreAllProjects-loadWrap">
          <button
            type="button"
            className="exploreAllProjects-loadBtn"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <>
                <span className="exploreAllProjects-spinner" aria-hidden />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <span>Load more</span>
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
};

export default ExploreAllProjects;